/**
 * Sagamoon Originals characters (Epic 20, AD-6).
 *
 * Hardcoded "set once, leave alone" roster of the **six** fixed example
 * characters — NOT a Firestore collection, and the final roster (S20.17: no
 * more characters are coming). Each carries a real Sagamoon portrait (`art`)
 * plus a full `origin` recipe that includes the original source image
 * (`sourceImageUrl`) shown as a before→after on the character detail page.
 *
 * These characters are a standalone showcase only — Originals **stories** no
 * longer reference them (the `characterSlug` link was removed in S20.17;
 * stories have one-off characters). The front-end resolves a character by slug
 * via `findOriginalsCharacter` for the home meet-cards + the character pages.
 *
 * Source content: docs/design/originals/Esimerkkihahmot/ (producer delivery).
 * Assets: apps/web/public/images/originals/ ({slug}.png + {slug}-original.jpg).
 *
 * @module data/originals-characters
 */

/** How a character was generated — shown in the inline "Miten tämä hahmo syntyi" recipe. */
export interface OriginalsCharacterOrigin {
  sourceLabel: string;
  /** Hosted original source-image URL (before→after recipe). S20.17. */
  sourceImageUrl: string;
  sourceCaption: string;
  promptLabel: string;
  promptText: string;
  personalityLabel: string;
  personality: string;
}

export interface OriginalsCharacter {
  /** Stable slug referenced by the character detail route. */
  slug: string;
  name: string;
  species: string;
  /** Short meet-card blurb. */
  blurb: string;
  /** Longer description shown on the detail hero. */
  description: string | null;
  /** Hosted character-portrait URL. */
  art: string | null;
  /** CSS color for the meet-card accent halo. */
  accent: string;
  /** Origin recipe (always set for the final six). */
  origin: OriginalsCharacterOrigin | null;
}

const SOURCE_LABEL = 'Alkuperäinen kuva';
const PROMPT_LABEL = 'Hahmon kuvaus';
const PERSONALITY_LABEL = 'Persoonallisuus';

export const ORIGINALS_CHARACTERS: OriginalsCharacter[] = [
  {
    slug: 'aida',
    name: 'Aida',
    species: 'Lohikäärmevarjon lapsi',
    blurb:
      'Rauhallinen, kaapuun pukeutunut lapsi seisoo silmät lempeästi suljettuina; takana kohoaa valtava lohikäärmeen varjo, joka vihjaa piilevästä rohkeudesta.',
    description:
      'Aida on rauhallinen, rohkea ja mielikuvituksellinen. Aina kun hän tarvitsee ylimääräistä rohkeutta, hän sulkee silmänsä ja antaa lohikäärmevarjonsa kohota korkealle.',
    art: '/images/originals/aida.png',
    accent: 'rgba(123, 94, 167, 0.40)',
    origin: {
      sourceLabel: SOURCE_LABEL,
      sourceImageUrl: '/images/originals/aida-original.jpg',
      sourceCaption: '',
      promptLabel: PROMPT_LABEL,
      promptText:
        'Aida asuu luostarissa, joka sijaitsee kaukaisella saarella. Aida on varovainen ja kohtelias, mutta hänellä on epätavallinen erityispiirre: hänen varjonsa on valtavan lohikäärmeen varjo, joka antaa Aidalle valtavat voimat, jos Aida on vaarassa tai suuttuu.',
      personalityLabel: PERSONALITY_LABEL,
      personality:
        'Rauhallinen, rohkea, mielikuvituksellinen. Sulkee silmänsä ja antaa lohikäärmevarjonsa kohota korkealle aina kun tarvitsee ylimääräistä rohkeutta.',
    },
  },
  {
    slug: 'olli',
    name: 'Olli',
    species: 'Metsän lapsi',
    blurb:
      'Haaveileva lapsi, jonka kiharoista kasvaa versoja kun hän unelmoi, ja jonka jokaisesta askeleesta nousee pieniä taimia.',
    description:
      'Olli on mielikuvituksellinen, lempeä ja hiljaisen päättäväinen. Joskus kun hän vähiten sitä toivoo, hänen askeleistaan puhkeaa pieniä taimia.',
    art: '/images/originals/olli.png',
    accent: 'rgba(167, 207, 154, 0.50)',
    origin: {
      sourceLabel: SOURCE_LABEL,
      sourceImageUrl: '/images/originals/olli-original.jpg',
      sourceCaption: '',
      promptLabel: PROMPT_LABEL,
      promptText:
        'Olli on hiljainen poika, joka yrittää parhaansa mukaan sulautua joukkoon, vaikka se on vaikeaa: hänen askeleensa jättää vihreän sammaleisen jalanjäljen juuri kun niin ei pitäisi tapahtua. Kun hän jännittää, hänen kiharoistaan puhkeaa pieniä lehtiä, ja mitä enemmän hän yrittää piilottaa niitä huppunsa alle, sitä sitkeämmin ne kasvavat esiin. Kasviongelmansa lisäksi Ollilla on toinenkin erikoisominaisuus — hän pystyy vaikuttamaan kasveihin ja niiden kasvuun.',
      personalityLabel: PERSONALITY_LABEL,
      personality:
        'Mielikuvituksellinen, lempeä, hiljaisen päättäväinen. Joskus kun hän vähiten sitä toivoo, hänen askeleistaan puhkeaa pieniä taimia.',
    },
  },
  {
    slug: 'muskeli-mikko',
    name: 'Muskeli Mikko',
    species: 'Lintubongari',
    blurb:
      'Muskeli Mikko on valtavan kokoinen, leveästi hymyilevä poika, jolla on jättimäiset käsivarret, punainen bandana ja musta paita. Ystävällinen mutta hätäinen lintubongari, joka yrittää ratkaista kaiken voimalla.',
    description:
      'Muskeli Mikko on ystävällinen, vahva ja lintuja rakastava. Hän yrittää usein ratkaista ongelmat voimalla, vaikkei se aina toimi — mutta lintuja tarkkaillessaan hän malttaa olla aivan hiljaa.',
    art: '/images/originals/muskeli-mikko.png',
    accent: 'rgba(224, 122, 95, 0.40)',
    origin: {
      sourceLabel: SOURCE_LABEL,
      sourceImageUrl: '/images/originals/muskeli-mikko-original.jpg',
      sourceCaption: '',
      promptLabel: PROMPT_LABEL,
      promptText:
        'Muskeli Mikko on suurikokoinen, mutta ystävällinen poika. Hän usein yrittää ratkoa ongelmat käyttäen voimaa, mutta se ei aina toimi. Muskeli Mikon lempiharrastus on lintubongaus.',
      personalityLabel: PERSONALITY_LABEL,
      personality:
        'Ystävällinen, vahva, lintuja rakastava. Yrittää usein ratkaista ongelmat voimalla, vaikkei se aina toimi.',
    },
  },
  {
    slug: 'zeemu',
    name: 'Zeemu',
    species: 'Avaruusolio',
    blurb:
      'Suurisilmäinen avaruusolio, jolla on iso soikea pää ja sammakko-apinamaiset raajat — hyppii korkealle ja on karhua vahvempi.',
    description:
      'Zeemu on ketterä, voimakas ja seikkailunhaluinen. Kun hän miettii uusia mahdollisuuksia, hän pomppii paikallaan sarjan nopeita, korkeita hyppyjä kuin väistelisi rauhallisesti näkymättömiä asteroideja.',
    art: '/images/originals/zeemu.png',
    accent: 'rgba(123, 94, 167, 0.45)',
    origin: {
      sourceLabel: SOURCE_LABEL,
      sourceImageUrl: '/images/originals/zeemu-original.jpg',
      sourceCaption: '',
      promptLabel: PROMPT_LABEL,
      promptText:
        'Zeemu on alieni, joka muistuttaa hieman sammakon ja apinan risteytystä. Zeemu osaa hyppiä korkealle ja pitkälle, ja hänen kätensä ovat vahvemmat kuin karhulla. Zeemun kotiplaneetta on asteroidivyöhykkeen lähellä, minkä takia hänestä on täytynyt tulla ketterä ja voimakas. Hän kuitenkin kyllästyi asteroidien väistelyyn ja lähti ulkoavaruuteen etsimään uusia mahdollisuuksia.',
      personalityLabel: PERSONALITY_LABEL,
      personality:
        'Ketterä, voimakas, seikkailunhaluinen. Pomppii paikallaan nopeita, korkeita hyppyjä, kun hän miettii uusia mahdollisuuksia.',
    },
  },
  {
    slug: 'piipu',
    name: 'Piipu',
    species: 'Pingviini',
    blurb:
      'Piipu on pieni, pehmoinen harmaa pingviini, jolla on kirkkaat siniset silmät, iloinen keltainen nokka ja pieni rusetti.',
    description: 'Vähän ujo mutta utelias Piipu haaveilee rohkeammaksi tulemisesta joka päivä.',
    art: '/images/originals/piipu.png',
    accent: 'rgba(249, 168, 37, 0.35)',
    origin: {
      sourceLabel: SOURCE_LABEL,
      sourceImageUrl: '/images/originals/piipu-original.jpg',
      sourceCaption: '',
      promptLabel: PROMPT_LABEL,
      promptText: 'Piipu on pieni pingviini, joka haluaisi olla rohkeampi.',
      personalityLabel: PERSONALITY_LABEL,
      personality: 'Ujo, utelias, haluaa olla rohkeampi.',
    },
  },
  {
    slug: 'jeti',
    name: 'Jeti',
    species: 'Koira',
    blurb:
      'Jeti on korkea valkoinen koira, jolla on tarkkaavaiset pystyt korvat ja lämpimät tummat silmät.',
    description:
      'Iloinen, utelias ja aina valmis tutkimaan, hän muuttaa jokaisen metsäpolun uudeksi seikkailuksi.',
    art: '/images/originals/jeti.png',
    accent: 'rgba(232, 196, 168, 0.45)',
    origin: {
      sourceLabel: SOURCE_LABEL,
      sourceImageUrl: '/images/originals/jeti-original.jpg',
      sourceCaption: '',
      promptLabel: PROMPT_LABEL,
      promptText: 'Jeti on iloinen karvakuono, joka tykkää tutkia ja seikkailla.',
      personalityLabel: PERSONALITY_LABEL,
      personality: 'Iloinen, utelias, seikkailunhaluinen.',
    },
  },
];

/** Resolve a character by slug. Returns undefined for an unknown slug. */
export function findOriginalsCharacter(slug: string): OriginalsCharacter | undefined {
  return ORIGINALS_CHARACTERS.find((c) => c.slug === slug);
}
